import React, { useState } from "react";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input";
import { CriteriaEditor } from "./CriteriaEditor";
import {
  composeCriteria,
  criteriaError,
  splitCriteria,
  type CriterionPrefix,
} from "../utils/criteria";

interface CreateEvaluatorFormProps {
  isSubmitting: boolean;
  /** Pre-fills the editor when the user starts from an example. */
  initialCriteria?: string;
  onSave: (criteria: string, name?: string) => void;
  onCancel: () => void;
  /** Reports whether there is unsaved input, so the parent can guard navigation. */
  onDirtyChange?: (isDirty: boolean) => void;
}

export function CreateEvaluatorForm({
  isSubmitting,
  initialCriteria = "",
  onSave,
  onCancel,
  onDirtyChange,
}: CreateEvaluatorFormProps) {
  const initial = splitCriteria(initialCriteria);
  const [prefix, setPrefix] = useState<CriterionPrefix | null>(initial.prefix);
  const [body, setBody] = useState(initial.body);
  const [name, setName] = useState("");

  const error = criteriaError(prefix, body);
  const canSave = !error && !isSubmitting;

  const markDirty = () => onDirtyChange?.(true);

  const handleSave = () => {
    if (!canSave) return;
    const trimmedName = name.trim();
    onSave(composeCriteria(prefix, body), trimmedName || undefined);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="max-w-3xl w-full px-6 py-6 flex flex-col gap-5">
        <div>
          <h2 className="text-xl font-bold text-foreground">New evaluator</h2>
          <p className="text-sm text-muted-foreground mt-1">
            One criterion, written in plain English. Composo scores every trace you send
            against it.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="evaluator-name">
            Name{" "}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <Input
            id="evaluator-name"
            value={name}
            disabled={isSubmitting}
            onChange={(e) => {
              setName(e.target.value);
              markDirty();
            }}
            placeholder="refund_policy_accuracy"
            className="font-mono max-w-md"
          />
          <p className="text-xs text-muted-foreground">
            This is how you reference the evaluator from the API. Leave it blank and one
            will be generated for you. It cannot be changed later.
          </p>
        </div>

        <CriteriaEditor
          prefix={prefix}
          body={body}
          autoFocus
          disabled={isSubmitting}
          error={null}
          onPrefixChange={(next) => {
            setPrefix(next);
            markDirty();
          }}
          onBodyChange={(next) => {
            setBody(next);
            markDirty();
          }}
        />

        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!canSave}
            title={error ?? undefined}
          >
            {isSubmitting ? "Creating…" : "Create evaluator"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {error && <span className="text-xs text-muted-foreground">{error}</span>}
        </div>
      </div>
    </div>
  );
}
